/**
 * Manufacturer Field Registry
 * Single source of truth for all Manufacturer field definitions
 * Used by adapters to generate DataGrid columns, Advanced Search configs, and Modal fields
 */

import { Manufacturer } from '../../services/api/domains/manufacturers/types';
import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';

// Type alias for type-safe field definitions
type ManufacturerFieldDefinition = BaseFieldDefinition<Manufacturer>;

/**
 * Complete Manufacturer Field Registry
 * Order matches typical display priority
 *
 * NOTE: The following fields are intentionally excluded or have special handling:
 * - manufacturer_id: Auto-generated, used as rowId, shown only in modal view mode
 * - product_count: Computed field derived from products.length
 * - facility_count: Computed field derived from facilities.length
 */
export const MANUFACTURER_FIELDS_REGISTRY: ManufacturerFieldDefinition[] = [
  // ========== Core Information ==========
  {
    key: 'manufacturer_name',
    label: 'Manufacturer Name',
    dataType: 'string',
    isRequired: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    validation: {
      minLength: 2,
      maxLength: 255,
    },
    renderConfig: {
      width: 300,
      flex: 1,
    },
  },

  // ========== Relational Fields ==========
  {
    key: 'facilities',
    label: 'Facilities',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isEditable: true, // Can be edited in modal
    isSortable: false,
    isFilterable: true,
    isSearchable: true, // Flattened to facility_name for search
    relationConfig: {
      type: 'many-to-many',
      entityName: 'facilities',
      idField: 'facility_id',
      displayField: 'facility_name',
      searchField: 'facility_name',
      dataSource: 'api',
      apiEndpoint: '/facilities',
    },
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
    },
  },
  {
    key: 'products',
    label: 'Products',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isEditable: true, // Can be edited in modal
    isSortable: false,
    isFilterable: true,
    isSearchable: true, // Could add product_name search in future
    description: 'Products associated with this manufacturer',
    relationConfig: {
      type: 'many-to-many',
      entityName: 'products',
      idField: 'product_id',
      displayField: 'product_name',
      searchField: 'product_name',
      preferredDisplayField: 'preferred_product_name', // Fallback pattern
      dataSource: 'api',
      apiEndpoint: '/products',
    },
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
    },
  },

  // ========== Computed Fields ==========
  {
    key: 'product_count',
    label: 'Product Count',
    dataType: 'number',
    isComputed: true,
    computedFrom: 'products',
    isSortable: true,
    isFilterable: true, // Backend supports COUNT subquery filtering
    isSearchable: true, // Available in advanced search
    isEditable: false,
    description: 'Number of products associated with this manufacturer',
    renderConfig: {
      width: 150,
    },
  },
  {
    key: 'facility_count',
    label: 'Facility Count',
    dataType: 'number',
    isComputed: true,
    computedFrom: 'facilities',
    isSortable: true,
    isFilterable: true, // Backend supports COUNT subquery filtering
    isSearchable: true, // Available in advanced search
    isEditable: false,
    description: 'Number of facilities associated with this manufacturer',
    renderConfig: {
      width: 150,
    },
  },

  // ========== Metadata Fields ==========
  {
    key: 'user_id',
    label: 'Created By',
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
];

// Create field helpers using shared utility
const helpers = createFieldHelpers<Manufacturer>(MANUFACTURER_FIELDS_REGISTRY);

export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
